/**
 * Alchemy Gardens — Native Fulfillment Widget
 *
 * Handles store pickup (Thu–Sat weekly) and local delivery (Sun weekly) scheduling.
 * Saves selections as cart attributes via the Shopify cart API.
 * Supports multiple instances (cart drawer + cart page).
 *
 * Schedule config: update AG_CONFIG if days/lead time change.
 */

(function () {
  'use strict';

  /* ── Schedule Configuration ──────────────────────────────────── */
  var AG_CONFIG = {
    pickup: {
      days: [4, 5, 6],                  // Thu, Fri, Sat (recurring weekly)
      note: '⭐ Pickup available Thu, Fri & Sat • 11AM–4PM ⭐',
      label: 'Farm Pickup'
    },
    delivery: {
      days: [0],                         // Sunday (recurring weekly)
      note: '⭐ Delivery available every Sunday • 11AM–4PM ⭐',
      label: 'Local Delivery'
    },
    leadDays: 1,                        // minimum days from today before first selectable date
    deliveryMinCents: 10000             // $100.00 minimum for delivery orders
  };

  var MONTHS = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];
  var DAYS_SHORT = ['Su','Mo','Tu','We','Th','Fr','Sa'];
  var DAYS_LONG  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  var agClearedThisLoad = false;        /* fires stale-wipe once per page load */

  /* ── Delivery zone zip codes (fallback if Liquid inline script doesn't execute) ── */
  if (!window.agDeliveryZips || !window.agDeliveryZips.length) {
    window.agDeliveryZips = [
      "74008","74011","74012","74013","74014","74015","74033","74037","74041","74043",
      "74047","74050","74055","74063","74066","74067","74073","74101","74102","74103",
      "74104","74105","74106","74107","74108","74110","74112","74114","74115","74116",
      "74117","74119","74120","74121","74126","74127","74128","74129","74130","74131",
      "74132","74133","74134","74135","74136","74137","74141","74145","74146","74147",
      "74148","74149","74150","74152","74153","74155","74156","74157","74158","74159",
      "74169","74170","74171","74172","74182","74186","74187","74192","74193","74429"
    ];
  }

  /*
   * agInitialized — WeakSet of widget DOM nodes that have been fully set up.
   *
   * Why WeakSet instead of a DOM attribute (data-ag-init)?
   *
   *   morphdom updates widget nodes IN PLACE when the cart section re-renders.
   *   "In place" means the same JavaScript object — the same DOM element
   *   reference — with its attributes and children updated to match the new
   *   server HTML.  Because the object identity never changes, a WeakSet check
   *   (agInitialized.has(el)) correctly returns true for an existing, already-
   *   initialized widget even after morphdom has finished its diff.
   *
   *   A DOM attribute (data-ag-init) is stripped by morphdom (the server HTML
   *   never includes it), which previously triggered the MutationObserver and
   *   caused a second initWidget() call on the same element — doubling every
   *   event listener.  The double dateDisplay.click listener opened the calendar
   *   and immediately closed it in the same tick: "no reaction, no calendar."
   *
   *   WeakSet entries are automatically GC-eligible once the DOM node is
   *   removed, so there is no memory leak across many cart updates.
   */
  var agInitialized = new WeakSet();

  /* ── Cart attribute save (note writing removed — attributes only) ── */
  function saveToCart(attrs) {
    var payload = { attributes: attrs };
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).catch(function (e) {
      console.warn('[AG] Could not save cart attributes:', e);
    });
  }

  /* ── Widget instance factory ────────────────────────────────── */
  function initWidget(el) {

    /*
     * AbortController — cancels ALL event listeners attached to this element
     * (and to external elements like the checkout button) in a single .abort()
     * call.  Belt-and-suspenders alongside the WeakSet guard: even if
     * initWidget() were somehow called twice on the same node, the second call
     * immediately cancels the first call's listeners before adding its own,
     * so there is always exactly ONE active listener set per widget.
     */
    if (el._agAbortCtrl) el._agAbortCtrl.abort();
    var _ctrl = new AbortController();
    el._agAbortCtrl = _ctrl;
    var _sig = { signal: _ctrl.signal };

    /* DOM refs scoped to this widget instance */
    var toggleBtns   = el.querySelectorAll('.ag-method-btn');
    var locationCard = el.querySelector('.ag-location-card');
    var zipSection   = el.querySelector('.ag-zip-section');
    var zipInput     = el.querySelector('.ag-zip-input');
    var zipMsg       = el.querySelector('.ag-zip-msg');
    var zipBtn       = el.querySelector('.ag-zip-btn');
    var calWrap      = el.querySelector('.ag-calendar-wrap');
    var calGrid      = el.querySelector('.ag-calendar-grid');
    var calTitle     = el.querySelector('.ag-calendar-title');
    var schedNote    = el.querySelector('.ag-schedule-note');
    var dateDisplay  = el.querySelector('.ag-date-display');
    var dateText     = el.querySelector('.ag-date-text');
    var prevBtn      = el.querySelector('.ag-cal-prev');
    var nextBtn      = el.querySelector('.ag-cal-next');
    var freeBarText  = el.querySelector('.ag-free-bar-text');
    var freeBarFill  = el.querySelector('.ag-free-bar-fill');
    var calBackdrop  = el.querySelector('.ag-cal-backdrop');
    var calCloseBtn  = el.querySelector('.ag-cal-close');
    var addressSection = el.querySelector('.ag-address-section');
    var addressStreet  = el.querySelector('.ag-address-street');
    var addressCity    = el.querySelector('.ag-address-city');
    var addressState   = el.querySelector('.ag-address-state');

    /* Find the checkout button — walk up the DOM (up to 6 levels) to handle
     * both the cart page and cart drawer DOM structures, where the button
     * may live in a sibling subtree several levels above the widget. */
    var checkoutBtn = null;
    var _anc = el.parentElement;
    for (var _i = 0; _i < 6 && _anc && !checkoutBtn; _i++) {
      checkoutBtn = _anc.querySelector('.cart__checkout-button');
      _anc = _anc.parentElement;
    }

    /* Instance state */
    var state = {
      method:       'pickup',
      year:         new Date().getFullYear(),
      month:        new Date().getMonth(),
      selectedDate: null,
      zipValidated: false
    };

    /* ── Event wiring ─────────────────────────────────────────── */
    toggleBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setMethod(btn.getAttribute('data-method'));
      }, _sig);
    });

    if (zipBtn) {
      zipBtn.addEventListener('click', checkZip, _sig);
    }

    if (zipInput) {
      zipInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); checkZip(); }
      }, _sig);
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { navigateMonth(-1); }, _sig);
    if (nextBtn) nextBtn.addEventListener('click', function () { navigateMonth(1); }, _sig);

    if (dateDisplay) {
      dateDisplay.addEventListener('click', function () {
        if (!calWrap) return;
        if (calWrap.classList.contains('ag-open')) {
          closeCalendar();
        } else {
          showCalendar();
        }
      }, _sig);
    }

    if (calBackdrop) calBackdrop.addEventListener('click', closeCalendar, _sig);
    if (calCloseBtn) calCloseBtn.addEventListener('click', closeCalendar, _sig);

    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', guardCheckout, _sig);
    }

    /* ── Method toggle ─────────────────────────────────────────── */
    function setMethod(method) {
      state.method = method;
      state.selectedDate = null;
      state.zipValidated = (method === 'pickup');

      /* Update toggle button states */
      toggleBtns.forEach(function (btn) {
        var isActive = btn.getAttribute('data-method') === method;
        btn.classList.toggle('ag-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      /* Reset date display and summary */
      if (dateText) dateText.textContent = 'Pick a date';
      clearSummary();

      /* Show/hide sections */
      if (method === 'pickup') {
        if (locationCard)   locationCard.style.display   = '';
        if (zipSection)     zipSection.style.display     = 'none';
        if (addressSection) addressSection.style.display = 'none';
        if (zipMsg)         zipMsg.textContent            = '';
        /* Ensure date row is visible (delivery gate may have hidden it) */
        var dateRow = el.querySelector('.ag-date-display');
        if (dateRow) dateRow.style.display = '';
        showDateRow();
        /* Clear stale delivery attributes from cart immediately */
        saveToCart({
          'Fulfillment Method': '',
          'Fulfillment Date': '',
          'Fulfillment Time': '',
          'Delivery Zip': '',
          'Delivery Address': ''
        });
      } else {
        if (locationCard) locationCard.style.display  = 'none';
        if (zipSection)   zipSection.style.display    = '';
        closeCalendar();
        /* Clear stale pickup attributes from cart immediately */
        saveToCart({
          'Fulfillment Method': '',
          'Fulfillment Date': '',
          'Fulfillment Time': '',
          'Delivery Zip': '',
          'Delivery Address': ''
        });
        /* Auto-revalidate if a complete zip is already in the input,
         * so switching back to delivery doesn't force a redundant re-check. */
        if (zipInput && /^\d{5}$/.test(zipInput.value.trim())) {
          checkZip();
        }
      }
      updateFreeBar();
      updateDeliveryMinGate();
      updateCheckoutState();
    }

    /* ── Zip validation ────────────────────────────────────────── */
    function checkZip() {
      var zip = zipInput ? zipInput.value.trim() : '';
      if (!/^\d{5}$/.test(zip)) {
        showZipMsg('Please enter a 5-digit zip code.', false);
        return;
      }

      /*
       * Zip codes are inlined by Liquid into window.agDeliveryZips — no CDN
       * fetch needed.  This makes validation instant and immune to CDN/upload
       * issues that previously caused "agZonesUrl not set" errors.
       */
      var zones = window.agDeliveryZips;
      if (!Array.isArray(zones) || zones.length === 0) {
        console.error('[AG] agDeliveryZips not set — check ag-fulfillment-widget.liquid');
        showZipMsg('Could not verify delivery. Please try again.', false);
        return;
      }

      if (zones.indexOf(zip) !== -1) {
        showZipMsg('Local delivery available.', true);
        state.zipValidated = true;
        if (addressSection) addressSection.style.display = '';
        var dateRow = el.querySelector('.ag-date-display');
        if (dateRow) dateRow.style.display = '';
        updateCheckoutState();
        showDateRow();
      } else {
        showZipMsg('Sorry, outside our delivery zone.', false);
        state.zipValidated = false;
        if (addressSection) addressSection.style.display = 'none';
        var dateRow = el.querySelector('.ag-date-display');
        if (dateRow) dateRow.style.display = 'none';
        closeCalendar();
        state.selectedDate = null;
        if (dateText) dateText.textContent = 'Pick a date';
        clearSummary();
        updateCheckoutState();
      }
    }

    function showZipMsg(msg, success) {
      if (!zipMsg) return;
      zipMsg.textContent = msg;
      zipMsg.className   = 'ag-zip-msg ' + (success ? 'ag-zip-success' : 'ag-zip-error');
    }

    /* ── Calendar display ─────────────────────────────────────── */
    function openCalendar() {
      /* Belt-and-suspenders: re-query if calWrap/calBackdrop ended up null at init time */
      if (!calWrap)     calWrap     = el.querySelector('.ag-calendar-wrap');
      if (!calBackdrop) calBackdrop = el.querySelector('.ag-cal-backdrop');
      if (calWrap)     calWrap.classList.add('ag-open');
      if (calBackdrop) calBackdrop.classList.add('ag-open');
    }

    function closeCalendar() {
      /* Belt-and-suspenders: re-query if calWrap/calBackdrop ended up null at init time */
      if (!calWrap)     calWrap     = el.querySelector('.ag-calendar-wrap');
      if (!calBackdrop) calBackdrop = el.querySelector('.ag-cal-backdrop');
      if (calWrap)     calWrap.classList.remove('ag-open');
      if (calBackdrop) calBackdrop.classList.remove('ag-open');
    }

    /* ── Auto-advance to first month with available dates ────── */
    function advanceToAvailableMonth() {
      var allowedDays = AG_CONFIG[state.method].days;
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      var minDate = new Date(today);
      minDate.setDate(today.getDate() + AG_CONFIG.leadDays);

      /* Check up to 3 months ahead for an available date */
      for (var offset = 0; offset < 3; offset++) {
        var y = state.year;
        var m = state.month + offset;
        if (m > 11) { m -= 12; y++; }
        var daysInMonth = new Date(y, m + 1, 0).getDate();
        for (var d = 1; d <= daysInMonth; d++) {
          var cellDate = new Date(y, m, d);
          var dow = cellDate.getDay();
          var methodCfg = AG_CONFIG[state.method];
          var avail;
          if (methodCfg.specificDates) {
            var isoCheck = y + '-' + pad(m + 1) + '-' + pad(d);
            avail = methodCfg.specificDates.indexOf(isoCheck) !== -1 && cellDate >= minDate;
          } else {
            avail = allowedDays.indexOf(dow) !== -1 && cellDate >= minDate;
          }
          if (avail) {
            state.year = y;
            state.month = m;
            return;
          }
        }
      }
    }

    function showDateRow() {
      advanceToAvailableMonth();
      renderCalendar(state.year, state.month);   /* pre-render silently so tap is instant */
      /* ag-open no longer needed — date row is always visible via CSS default */
    }

    function showCalendar() {
      advanceToAvailableMonth();
      openCalendar();
      renderCalendar(state.year, state.month);
    }

    function renderCalendar(year, month) {
      state.year  = year;
      state.month = month;

      if (calTitle) {
        calTitle.textContent = MONTHS[month] + ' ' + year;
      }

      if (schedNote) {
        schedNote.textContent = AG_CONFIG[state.method].note;
      }

      var allowedDays = AG_CONFIG[state.method].days;

      /* Minimum selectable date: today + leadDays */
      var today   = new Date();
      today.setHours(0, 0, 0, 0);
      var minDate = new Date(today);
      minDate.setDate(today.getDate() + AG_CONFIG.leadDays);

      var firstDow     = new Date(year, month, 1).getDay();
      var daysInMonth  = new Date(year, month + 1, 0).getDate();
      var selectedTime = state.selectedDate ? state.selectedDate.getTime() : null;

      var html = '';

      /* Day-of-week headers */
      DAYS_SHORT.forEach(function (d) {
        html += '<div class="ag-day ag-day--header" aria-hidden="true">' + d + '</div>';
      });

      /* Leading blank cells */
      for (var i = 0; i < firstDow; i++) {
        html += '<div class="ag-day ag-day--empty" aria-hidden="true"></div>';
      }

      /* Day cells */
      for (var d = 1; d <= daysInMonth; d++) {
        var cellDate = new Date(year, month, d);
        var dow      = cellDate.getDay();
        var methodCfg = AG_CONFIG[state.method];
        var avail;
        if (methodCfg.specificDates) {
          var isoCheck = year + '-' + pad(month + 1) + '-' + pad(d);
          avail = methodCfg.specificDates.indexOf(isoCheck) !== -1 && cellDate >= minDate;
        } else {
          avail = allowedDays.indexOf(dow) !== -1 && cellDate >= minDate;
        }
        var selected = selectedTime && cellDate.getTime() === selectedTime;
        var iso      = year + '-' + pad(month + 1) + '-' + pad(d);

        var cls = 'ag-day';
        if (avail)    cls += ' ag-day--available';
        else          cls += ' ag-day--disabled';
        if (selected) cls += ' ag-day--selected';

        var label = DAYS_LONG[dow] + ', ' + MONTHS[month] + ' ' + d;
        html += '<div class="' + cls + '" data-date="' + iso + '" role="gridcell"'
             + (avail ? ' tabindex="0" aria-label="' + label + '"' : ' aria-disabled="true"')
             + '>' + d + '</div>';
      }

      if (calGrid) {
        calGrid.innerHTML = html;

        /* Wire up available day clicks + keyboard */
        calGrid.querySelectorAll('.ag-day--available').forEach(function (cell) {
          cell.addEventListener('click',   function () { selectDay(cell); });
          cell.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectDay(cell); }
          });
        });
      }
    }

    /* ── Date selection ───────────────────────────────────────── */
    function selectDay(cell) {
      var iso   = cell.getAttribute('data-date');
      var parts = iso.split('-');
      var date  = new Date(
        parseInt(parts[0], 10),
        parseInt(parts[1], 10) - 1,
        parseInt(parts[2], 10)
      );
      state.selectedDate = date;

      /* Display text */
      var display = DAYS_LONG[date.getDay()] + ', '
                  + MONTHS[date.getMonth()] + ' ' + date.getDate()
                  + ', ' + date.getFullYear();

      if (dateText) dateText.textContent = display;

      /* Update selected cell highlight without re-rendering */
      if (calGrid) {
        calGrid.querySelectorAll('.ag-day--selected').forEach(function (c) {
          c.classList.remove('ag-day--selected');
        });
        cell.classList.add('ag-day--selected');
      }

      /* Save to cart (attributes + note) */
      var attrs = {};
      var methodLabel = AG_CONFIG[state.method].label;
      var timeWindow = 'between 11AM and 4PM';
      attrs['Fulfillment Method'] = methodLabel;
      attrs['Fulfillment Date']   = display;
      attrs['Fulfillment Time']   = timeWindow;
      if (state.method === 'delivery') {
        if (zipInput && zipInput.value.trim()) attrs['Delivery Zip'] = zipInput.value.trim();
        var street = addressStreet ? addressStreet.value.trim() : '';
        var city   = addressCity   ? addressCity.value.trim()   : '';
        var st     = addressState  ? addressState.value.trim()  : '';
        var zip    = zipInput      ? zipInput.value.trim()      : '';
        if (street) {
          attrs['Delivery Address'] = street + ', ' + city + ' ' + st + ' ' + zip;
        }
      }
      saveToCart(attrs);

      /* Update visible summary + merged checkout button */
      updateSummary(methodLabel, display + '\nBetween 11AM and 4PM');

      /* Collapse calendar — date display row stays visible */
      closeCalendar();
      updateCheckoutState();
    }

    /* ── Fulfillment summary display ───────────────────────────── */
    var summaryEl = el.querySelector('.ag-fulfillment-summary');
    function updateSummary(method, dateStr) {
      /* Hide the standalone summary bar — info is now in the checkout button */
      if (summaryEl) summaryEl.style.display = 'none';

      /* Merge summary into checkout button */
      if (checkoutBtn) {
        var parts = dateStr.split('\n');
        var datePart = parts[0] || '';
        var timePart = parts[1] || '';
        checkoutBtn.innerHTML = '<div class="ag-checkout-merged">'
          + '<div class="ag-checkout-method">' + method + '</div>'
          + '<div class="ag-checkout-date">' + datePart + '</div>'
          + (timePart ? '<div class="ag-checkout-time">' + timePart + '</div>' : '')
          + '<div class="ag-checkout-label">CHECKOUT</div>'
          + '</div>';
        checkoutBtn.classList.add('ag-checkout-ready');
        checkoutBtn.classList.remove('ag-locked');
        checkoutBtn.disabled = false;
      }
    }
    function clearSummary() {
      if (summaryEl) summaryEl.style.display = 'none';

      /* Reset checkout button */
      if (checkoutBtn) {
        checkoutBtn.innerHTML = 'Check out';
        checkoutBtn.classList.remove('ag-checkout-ready');
      }
    }

    /* ── Month navigation ─────────────────────────────────────── */
    function navigateMonth(delta) {
      var m = state.month + delta;
      var y = state.year;
      if (m > 11) { m = 0; y++; }
      if (m < 0)  { m = 11; y--; }

      /* Block navigation before current month */
      var now = new Date();
      if (y < now.getFullYear() || (y === now.getFullYear() && m < now.getMonth())) return;

      renderCalendar(y, m);
    }

    /* ── Checkout guard ───────────────────────────────────────── */
    function guardCheckout(e) {
      var needsZip     = state.method === 'delivery' && !state.zipValidated;
      var needsDate    = !state.selectedDate;
      var needsAddress = state.method === 'delivery' && addressStreet && !addressStreet.value.trim();

      if (needsZip || needsDate || needsAddress) {
        e.preventDefault();
        /* Scroll to widget and flash a green border pulse */
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        el.style.transition  = 'box-shadow 0.15s ease';
        el.style.boxShadow   = '0 0 0 2px #7ac217';
        setTimeout(function () {
          el.style.boxShadow = '0 0 0 0 transparent';
          setTimeout(function () { el.style.boxShadow = ''; }, 300);
        }, 1000);

        /* Show helpful message */
        if (needsZip && zipMsg) {
          zipMsg.textContent  = 'Please verify your zip code first.';
          zipMsg.className    = 'ag-zip-msg ag-zip-error';
        } else if (needsAddress && addressStreet) {
          addressStreet.placeholder = 'Please enter your delivery address';
          addressStreet.focus();
        } else if (needsDate && dateText) {
          dateText.textContent = 'Please pick a date to continue.';
        }
        return;
      }

      /* ── Final data sync before checkout proceeds ─────────────
       * Re-save ALL current widget state to cart attributes as a last
       * defense against stale data.  This catches late address edits,
       * method changes, or any state that drifted from saved attributes. */
      var finalAttrs = {};
      var methodLabel = AG_CONFIG[state.method].label;
      var sd = state.selectedDate;
      var dateStr = sd ? (DAYS_LONG[sd.getDay()] + ', '
                  + MONTHS[sd.getMonth()] + ' ' + sd.getDate()
                  + ', ' + sd.getFullYear()) : '';
      finalAttrs['Fulfillment Method'] = methodLabel;
      finalAttrs['Fulfillment Date']   = dateStr;
      finalAttrs['Fulfillment Time']   = 'between 11AM and 4PM';
      if (state.method === 'delivery') {
        finalAttrs['Delivery Zip'] = zipInput ? zipInput.value.trim() : '';
        var street = addressStreet ? addressStreet.value.trim() : '';
        var city   = addressCity   ? addressCity.value.trim()   : '';
        var st     = addressState  ? addressState.value.trim()  : '';
        var zip    = zipInput      ? zipInput.value.trim()      : '';
        finalAttrs['Delivery Address'] = street ? (street + ', ' + city + ' ' + st + ' ' + zip) : '';
      } else {
        /* Ensure no delivery data leaks through on pickup orders */
        finalAttrs['Delivery Zip']     = '';
        finalAttrs['Delivery Address'] = '';
      }
      saveToCart(finalAttrs);
    }

    /* ── Delivery minimum gate ───────────────────────────────── */
    /* Hides entire delivery form (zip + address + date) until cart >= $100 minimum */
    function updateDeliveryMinGate() {
      if (state.method !== 'delivery') return;
      var total = typeof window.agCartTotal === 'number' ? window.agCartTotal : 0;
      var minCents = AG_CONFIG.deliveryMinCents;
      var dateRow = el.querySelector('.ag-date-display');

      if (total < minCents) {
        /* Hide entire delivery form block, close calendar */
        if (zipSection)     zipSection.style.display     = 'none';
        if (addressSection) addressSection.style.display = 'none';
        if (dateRow)        dateRow.style.display         = 'none';
        closeCalendar();
        state.selectedDate = null;
        if (dateText) dateText.textContent = 'Pick a date';
        clearSummary();
      } else {
        /* Show zip section; address + date only after zip validation */
        if (zipSection) zipSection.style.display = '';
        if (state.zipValidated) {
          if (addressSection) addressSection.style.display = '';
          if (dateRow) dateRow.style.display = '';
          showDateRow();
        } else {
          if (addressSection) addressSection.style.display = 'none';
          if (dateRow) dateRow.style.display = 'none';
        }
      }
    }

    /* ── Free delivery progress bar ──────────────────────────── */
    function renderFreeBar(totalCents) {
      if (!freeBarText || !freeBarFill) return;
      var minCents = AG_CONFIG.deliveryMinCents;
      var pct = Math.min(totalCents / minCents, 1);
      freeBarFill.style.width = (pct * 100).toFixed(1) + '%';

      /* Force visibility — setProperty('important') beats any theme !important CSS */
      freeBarText.style.setProperty('display',     'block',       'important');
      freeBarText.style.setProperty('font-size',   '1.0625rem',   'important');
      freeBarText.style.setProperty('font-weight', '600',         'important');
      freeBarText.style.setProperty('line-height', '1.4',         'important');
      freeBarText.style.setProperty('margin',      '0 0 0.5rem',  'important');
      freeBarText.style.setProperty('opacity',     '1',           'important');
      freeBarText.style.setProperty('visibility',  'visible',     'important');

      if (totalCents >= minCents) {
        freeBarText.textContent = '\ud83c\udf89 Delivery unlocked! Free delivery available.';
        freeBarText.style.setProperty('color',                   '#7ac217', 'important');
        freeBarText.style.setProperty('-webkit-text-fill-color', '#7ac217', 'important');
        freeBarFill.style.background = '#7ac217';
      } else {
        var needed = ((minCents - totalCents) / 100).toFixed(2);
        freeBarText.textContent = 'You\'re $' + needed + ' away from unlocking delivery (min $' + (minCents / 100) + ')';
        freeBarText.style.setProperty('color',                   '#fff', 'important');
        freeBarText.style.setProperty('-webkit-text-fill-color', '#fff', 'important');
        freeBarFill.style.background = 'rgba(255, 255, 255, 0.7)';
      }
    }

    function updateFreeBar() {
      if (!freeBarText) return;

      /* Hide progress bar entirely for pickup — only delivery has a minimum */
      if (state.method === 'pickup') {
        freeBarText.style.setProperty('display', 'none', 'important');
        if (freeBarFill) freeBarFill.style.width = '0%';
        return;
      }

      /* Render immediately using the server-known total (zero delay) */
      renderFreeBar(typeof window.agCartTotal === 'number' ? window.agCartTotal : 0);
      updateDeliveryMinGate();
      /* Then refresh live so post-load quantity changes are reflected */
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          window.agCartTotal = cart.total_price;   /* keep global in sync */
          renderFreeBar(cart.total_price);
          updateDeliveryMinGate();
          updateCheckoutState();
        })
        .catch(function () {});
    }

    /* ── Checkout lock state ──────────────────────────────────── */
    function updateCheckoutState() {
      if (!checkoutBtn) return;
      var hasAddress = state.method === 'pickup' || (addressStreet && addressStreet.value.trim());
      var meetsMin = state.method === 'pickup' ||
                     (typeof window.agCartTotal === 'number' && window.agCartTotal >= AG_CONFIG.deliveryMinCents);
      var ready = state.selectedDate !== null &&
                  (state.method === 'pickup' || state.zipValidated) &&
                  hasAddress && meetsMin;
      checkoutBtn.classList.toggle('ag-locked', !ready);
      checkoutBtn.disabled = !ready;
    }

    /* ── Post-morphdom DOM reconciliation ─────────────────────── */
    /*
     * morphdom resets the section HTML back to server defaults every time the
     * cart updates: pickup becomes active again, the checkout button loses its
     * ag-locked class, the zip section is hidden, and the date text reverts to
     * "Pick a date" — even though the customer had already made selections.
     *
     * The in-memory `state` object is untouched (WeakSet prevents re-init), so
     * all we need to do is re-apply that state back to the DOM.  This function
     * is called from the cart:update handler AFTER the 100 ms morphdom window.
     */
    function syncDOMState() {
      /* 1 — Method toggle buttons */
      toggleBtns.forEach(function (btn) {
        var isActive = btn.getAttribute('data-method') === state.method;
        btn.classList.toggle('ag-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      /* 2 — Location card / zip section / address section visibility */
      if (state.method === 'pickup') {
        if (locationCard)   locationCard.style.display   = '';
        if (zipSection)     zipSection.style.display     = 'none';
        if (addressSection) addressSection.style.display = 'none';
      } else {
        if (locationCard)   locationCard.style.display   = 'none';
        if (zipSection)     zipSection.style.display     = '';
        if (addressSection) addressSection.style.display = state.zipValidated ? '' : 'none';
      }

      /* 3 — Zip validation message (morphdom resets it to empty) */
      if (state.method === 'delivery' && state.zipValidated && zipMsg) {
        zipMsg.textContent = 'Local delivery available.';
        zipMsg.className   = 'ag-zip-msg ag-zip-success';
      }

      /* 4 — Selected date text */
      if (state.selectedDate && dateText) {
        var d = state.selectedDate;
        dateText.textContent = DAYS_LONG[d.getDay()] + ', '
                             + MONTHS[d.getMonth()] + ' '
                             + d.getDate() + ', ' + d.getFullYear();
      }

      /* 5 — Fulfillment summary */
      if (state.selectedDate) {
        var sd = state.selectedDate;
        updateSummary(
          AG_CONFIG[state.method].label,
          DAYS_LONG[sd.getDay()] + ', ' + MONTHS[sd.getMonth()] + ' ' + sd.getDate() + ', ' + sd.getFullYear()
        );
      }

      /* 6 — Checkout lock (morphdom renders button without ag-locked / disabled) */
      updateCheckoutState();

      /* 7 — Free delivery bar (morphdom wipes inline styles and text content) */
      updateFreeBar();
    }

    /* ── Kick off default state (Pickup) ──────────────────────── */
    /* ── Restore previous selections from cart attributes ────── */
    function restoreFromCart() {
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var attrs = cart.attributes || {};
          var method = attrs['Fulfillment Method'] || '';
          var date   = attrs['Fulfillment Date']   || '';
          var zip    = attrs['Delivery Zip']        || '';
          var addr   = attrs['Delivery Address']    || '';

          /* If we have a saved method, restore it */
          if (method.toLowerCase().indexOf('delivery') !== -1) {
            setMethod('delivery');
            /* Pre-fill zip but DON'T auto-validate — customer must click Check
             * to reveal address/date fields one step at a time */
            if (zip && zipInput) {
              zipInput.value = zip;
            }
          } else if (method.toLowerCase().indexOf('pickup') !== -1) {
            setMethod('pickup');
            /* Restore date if it's still in the future */
            if (date) {
              var parsedDate = new Date(date);
              if (!isNaN(parsedDate.getTime()) && parsedDate > new Date()) {
                state.selectedDate = parsedDate;
                if (dateText) {
                  dateText.textContent = DAYS_LONG[parsedDate.getDay()] + ', '
                    + MONTHS[parsedDate.getMonth()] + ' ' + parsedDate.getDate();
                }
                var methodLabel = AG_CONFIG[state.method].label;
                var displayDate = DAYS_LONG[parsedDate.getDay()] + ', '
                  + MONTHS[parsedDate.getMonth()] + ' ' + parsedDate.getDate()
                  + ', ' + parsedDate.getFullYear();
                updateSummary(methodLabel, displayDate + '\nBetween 11AM and 4PM');
              }
            }
          } else {
            /* No saved method — default to pickup */
            setMethod('pickup');
          }

          updateFreeBar();
          updateCheckoutState();
        })
        .catch(function () {
          /* Fallback: just default to pickup */
          setMethod('pickup');
          updateFreeBar();
        });
    }

    /* Restore previous selections from cart attributes, or default to pickup */
    if (!agClearedThisLoad) {
      agClearedThisLoad = true;
      restoreFromCart();
    } else {
      setMethod('pickup');
      updateFreeBar();
    }
    el._agUpdateFreeBar  = updateFreeBar;   /* expose for cart:update handler */
    el._agSyncDOMState   = syncDOMState;    /* expose for cart:update handler */
    attachDrawerListener();                 /* safe here: widget is live, drawer in DOM */
  }

  /* ── Helpers ────────────────────────────────────────────────── */
  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  /* ── Initialize all widget instances ───────────────────────── */
  function initAll() {
    document.querySelectorAll('.ag-fulfillment-widget').forEach(function (el) {
      /*
       * WeakSet check: morphdom updates widget nodes IN PLACE (same JS object
       * reference), so agInitialized.has(el) correctly returns true for an
       * already-initialized widget even after morphdom has re-rendered the
       * section around it.  Only truly NEW nodes (cart drawer first open, full
       * sectionRenderer replacement) are different objects not yet in the set.
       */
      if (agInitialized.has(el)) return;

      /* Guard: skip if children are still being injected (calendar wrap absent).
       * The MutationObserver will call initAll() again once they arrive. */
      if (!el.querySelector('.ag-calendar-wrap')) return;

      agInitialized.add(el);
      initWidget(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /*
   * On cart updates: refresh the free-delivery bar and pick up any brand-new
   * widget nodes (e.g. cart drawer first open, cart going empty → non-empty).
   *
   * Event name: 'cart:update' (no trailing 'd') — this is ThemeEvents.cartUpdate
   * as defined in assets/events.js.  CartUpdateEvent and CartAddEvent both
   * dispatch this name with { bubbles: true }, so it reaches document.
   *
   * This fires BEFORE morphSection() runs (see component-cart-items.js line
   * 165 vs 173), which is fine: updateFreeBar() starts an async /cart.js fetch
   * that will complete after morphdom finishes, so renderFreeBar() always
   * writes the correct total into the (already-morphdom'd) freeBarText node.
   *
   * We do NOT call initWidget() on existing widgets here.  morphdom updates
   * nodes IN PLACE (same JS object), so agInitialized.has(el) keeps returning
   * true for them — initAll() skips them safely.  Their event listeners and
   * state are preserved across cart updates, so a date/zip the customer already
   * selected stays selected after changing item quantities.
   */
  document.addEventListener('cart:update', function () {
    /*
     * Two separate timers — each targeting a different concern:
     *
     * setTimeout(fn, 0)
     *   Runs in the very next macrotask, which is AFTER morphdom finishes.
     *   morphdom executes synchronously inside the same .then() callback
     *   that dispatches cart:update; our handler returns immediately, so
     *   morphdom completes before the event loop picks up our macrotask.
     *   syncDOMState() restores the customer's method/date/zip selections
     *   AND refreshes the free bar.  Firing at 0 ms (rather than 100 ms)
     *   eliminates the visible pickup-button flash between morphdom and fix.
     *
     * setTimeout(initAll, 100)
     *   Allows AJAX-injected section HTML (cart drawer first open, full
     *   sectionRenderer replace) to arrive before scanning for new nodes.
     *   WeakSet makes initAll() a near-instant no-op on existing widgets.
     */
    setTimeout(function () {
      document.querySelectorAll('.ag-fulfillment-widget').forEach(function (w) {
        if (typeof w._agSyncDOMState === 'function') w._agSyncDOMState();
      });
    }, 0);
    setTimeout(initAll, 100);
  });

  /*
   * MutationObserver — catches widget nodes injected by cart drawer AJAX (first
   * open) and by full section replacements (sectionRenderer.renderSection).
   *
   * Debounced 50 ms so initAll() runs after the full subtree is in the DOM.
   * initAll() is idempotent (WeakSet guard), so firing it more often than
   * strictly necessary is harmless and costs only a fast WeakSet lookup.
   */
  var agObserverTimer = null;
  var agObserver = new MutationObserver(function () {
    clearTimeout(agObserverTimer);
    agObserverTimer = setTimeout(initAll, 50);
  });
  agObserver.observe(document.body, { childList: true, subtree: true });

  /*
   * Attaches a one-time 'dialog:open' listener to cart-drawer-component so
   * syncDOMState() (which includes updateFreeBar) runs every time the drawer
   * is opened — even when no cart change fires.  Called from initWidget() at
   * the end of first-time setup, guaranteeing cart-drawer-component is in the
   * DOM whether the section was server-rendered or AJAX-injected.
   */
  var agDrawerListenerAttached = false;

  function attachDrawerListener() {
    if (agDrawerListenerAttached) return;
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    agDrawerListenerAttached = true;
    /*
     * 'dialog:open' fires on cart-drawer-component (non-bubbling) every time
     * showDialog() runs — even when no cart change has occurred.
     *
     * syncDOMState() restores the customer's method/date/zip selections AND
     * refreshes the free delivery bar (step 6), so reopening the drawer after
     * browsing always shows the correct visual state without any grey bar.
     *
     * Called from initWidget() after the widget is fully set up, so
     * cart-drawer-component is guaranteed to be in the DOM at that moment
     * regardless of whether the section was server-rendered or AJAX-injected.
     */
    drawer.addEventListener('dialog:open', function () {
      document.querySelectorAll('.ag-fulfillment-widget').forEach(function (w) {
        if (typeof w._agSyncDOMState === 'function') w._agSyncDOMState();
      });
    });
  }

})();
